import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Search, Plus, Tag, User, ShoppingCart, Building, FileText, Bot, Megaphone, Heart, FolderOpen, Settings, Bell, MessageSquare, Briefcase, BarChart3, Shield, BookOpen, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import authStore from '../../store/authStore';

const DesktopSidebar = () => {
  const location = useLocation();
  const { user } = authStore();
  const { t } = useTranslation();

  const hiddenPaths = ['/login', '/register', '/verify-email', '/admin'];
  if (hiddenPaths.some(p => location.pathname.startsWith(p))) return null;
  if (location.pathname === '/' && !user) return null;

  const isProfessional = user?.is_professional || user?.user_type === 'professional';

  const mainLinks = [
    { path: '/', icon: Home, label: t('Home') },
    { path: '/search', icon: Search, label: t('Cerca') },
    { path: '/services', icon: Wrench, label: t('Servizi') },
    { path: '/marketplace', icon: ShoppingCart, label: t('Marketplace') },
    { path: '/properties', icon: Building, label: t('Immobili') },
    { path: '/requests', icon: FileText, label: t('Richieste') },
    { path: '/promotions', icon: Tag, label: t('Promo') },
    { path: '/inspiration', icon: Megaphone, label: t('Ispirazioni') },
    { path: '/rivista', icon: BookOpen, label: t('La Rivista') },
  ];

  const userLinks = user ? [
    { path: '/create', icon: Plus, label: t('Pubblica'), special: true },
    { path: '/notifications', icon: Bell, label: t('Notifiche') },
    { path: '/messages', icon: MessageSquare, label: t('Messaggi') },
    { path: '/favorites', icon: Heart, label: t('Preferiti') },
    { path: '/projects', icon: FolderOpen, label: 'Progetti' },
    { path: '/ai-chat', icon: Bot, label: 'Assistente AI' },
    ...(isProfessional ? [{ path: '/crm', icon: Briefcase, label: 'CRM' }] : []),
  ] : [];

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <aside className="hidden lg:flex flex-col w-[240px] bg-white border-r border-gray-200 h-[calc(100vh-64px)] sticky top-16 overflow-y-auto" data-testid="desktop-sidebar">
      <nav className="flex-1 py-3 px-3">
        <div className="mb-1">
          {mainLinks.map(link => (
            <Link key={link.path} to={link.path} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition mb-0.5 ${isActive(link.path) ? 'bg-emerald-50 text-[#10B981] font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
              <link.icon className={`w-[20px] h-[20px] ${isActive(link.path) ? 'text-[#10B981]' : 'text-gray-400'}`} strokeWidth={isActive(link.path) ? 2.5 : 1.5} />
              {link.label}
            </Link>
          ))}
        </div>

        {userLinks.length > 0 && (
          <>
            <div className="border-t border-gray-100 my-2" />
            {userLinks.map(link => (
              <Link key={link.path} to={link.path} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition mb-0.5 ${link.special ? 'bg-[#10B981] text-white hover:bg-[#059669]' : isActive(link.path) ? 'bg-emerald-50 text-[#10B981] font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
                <link.icon className={`w-[20px] h-[20px] ${link.special ? 'text-white' : isActive(link.path) ? 'text-[#10B981]' : 'text-gray-400'}`} strokeWidth={link.special || isActive(link.path) ? 2.5 : 1.5} />
                {link.label}
              </Link>
            ))}
          </>
        )}

        {user && (
          <>
            <div className="border-t border-gray-100 my-2" />
            <Link to="/profile" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${isActive('/profile') ? 'bg-emerald-50 text-[#10B981]' : 'text-gray-600 hover:bg-gray-50'}`}>
              <div className="w-[20px] h-[20px] rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] font-bold text-gray-500">{user.name?.charAt(0)}</span>}
              </div>
              Il mio Profilo
            </Link>
          </>
        )}
      </nav>
    </aside>
  );
};

export default DesktopSidebar;
